import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, AlertCircle, Upload, MessageCircle } from "lucide-react";
import { api, formatApiError } from "@/api";
import { useSite } from "@/context/SiteContext";
import { usePageContent } from "@/context/PageContentContext";
import { buildProductDevMessage, openWhatsApp } from "@/lib/whatsappMessages";

export default function ProductDevelopment() {
  const { content: site } = useSite();
  const { content } = usePageContent("product-dev");
  const newProductItems = content.new_product_items || [];
  const improvementItems = content.improvement_items || [];
  const allCategories = [...newProductItems, ...improvementItems, "Other"];

  const [form, setForm] = useState({
    company_name: "", contact_person: "", mobile: "", email: "",
    product_category: newProductItems[0] || "Other", product_description: "", sample_file_name: "",
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await api.post("/leads/product-dev", form);
      openWhatsApp(site.whatsapp, buildProductDevMessage(form));
      setStatus({
        state: "success",
        msg: "Inquiry saved. WhatsApp opened in a new tab — continue the conversation there.",
      });
      setForm({ company_name: "", contact_person: "", mobile: "", email: "",
        product_category: newProductItems[0] || "Other", product_description: "", sample_file_name: "" });
    } catch (err) {
      setStatus({ state: "error", msg: formatApiError(err?.response?.data?.detail) });
    }
  };

  return (
    <div data-testid="page-product-development" className="bg-cream">
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-[var(--k-olive-soft)] opacity-35 blur-[100px]" />
        <div className="container-k px-6 md:px-12 relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-4xl">
            <div className="font-mono-label text-[var(--k-green)] mb-5">{content.hero_eyebrow}</div>
            <h1 data-testid="pd-headline" className="font-serif-display text-4xl sm:text-5xl md:text-7xl text-[var(--k-text)] leading-[1.02]">
              {content.hero_title_pre} <em className="green-gradient-text not-italic">{content.hero_title_em}</em> {content.hero_title_post}
            </h1>
            <p className="mt-7 text-lg text-[var(--k-text-2)] max-w-2xl leading-relaxed">
              {content.hero_intro}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section bg-beige" data-testid="pd-services">
        <div className="container-k grid lg:grid-cols-2 gap-10">
          <ServiceList title={content.new_product_title} items={newProductItems} testIdPrefix="pd-new" />
          <ServiceList title={content.improvement_title} items={improvementItems} testIdPrefix="pd-imp" />
        </div>
      </section>

      <section className="section bg-cream" data-testid="pd-form-section">
        <div className="container-k grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="font-mono-label text-[var(--k-gold)] mb-4">{content.form_eyebrow}</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl text-[var(--k-text)] leading-tight">
              {content.form_title_pre} <em className="green-gradient-text not-italic">{content.form_title_em}</em>{content.form_title_post}
            </h2>
            <p className="mt-6 text-[var(--k-text-2)] leading-relaxed max-w-md">
              {content.form_intro}
            </p>
          </div>

          <form onSubmit={submit} className="organic-card p-8 md:p-12 lg:col-span-7" data-testid="pd-form">
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
              <Field label="Company Name *">
                <input className="kostra-input" required value={form.company_name} onChange={update("company_name")} data-testid="pd-input-company" />
              </Field>
              <Field label="Contact Person *">
                <input className="kostra-input" required value={form.contact_person} onChange={update("contact_person")} data-testid="pd-input-contact" />
              </Field>
              <Field label="Mobile *">
                <input className="kostra-input" type="tel" required value={form.mobile} onChange={update("mobile")} data-testid="pd-input-mobile" placeholder="+91 99999 00000" />
              </Field>
              <Field label="Email *">
                <input className="kostra-input" type="email" required value={form.email} onChange={update("email")} data-testid="pd-input-email" placeholder="you@company.com" />
              </Field>
              <Field label="Product Category" full>
                <select className="kostra-input cursor-pointer" value={form.product_category} onChange={update("product_category")} data-testid="pd-select-category" style={{ background: "var(--k-card)" }}>
                  {allCategories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Product Description" full>
                <textarea className="kostra-input resize-none" rows={4} value={form.product_description} onChange={update("product_description")} data-testid="pd-input-description" placeholder="Tell us about the product, target audience, distribution channel…" />
              </Field>
              <Field label="Sample File (filename or link, optional)" full>
                <div className="flex items-center gap-3">
                  <Upload className="w-4 h-4 text-[var(--k-muted)]" />
                  <input className="kostra-input" value={form.sample_file_name} onChange={update("sample_file_name")} data-testid="pd-input-sample" placeholder="brief.pdf or link to drive" />
                </div>
              </Field>
            </div>

            {status.state === "success" && (
              <StatusBanner ok msg={status.msg} testId="pd-form-success" />
            )}
            {status.state === "error" && (
              <StatusBanner msg={status.msg} testId="pd-form-error" />
            )}

            <button type="submit" disabled={status.state === "loading"} className="btn-green mt-10" data-testid="pd-submit-btn">
              {status.state === "loading" ? "Submitting…" : (<>Send to WhatsApp <MessageCircle className="w-4 h-4" /></>)}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <label className={`flex flex-col gap-2 ${full ? "md:col-span-2" : ""}`}>
      <span className="font-mono-label text-[var(--k-muted)]">{label}</span>
      {children}
    </label>
  );
}

function ServiceList({ title, items, testIdPrefix }) {
  return (
    <div className="organic-card p-10">
      <h3 className="font-serif-display text-3xl text-[var(--k-text)] mb-6">{title}</h3>
      <ul className="space-y-3">
        {items.map((s, i) => (
          <li key={s} data-testid={`${testIdPrefix}-${i}`} className="flex items-center gap-3 text-[var(--k-text-2)]">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--k-green)]" />
            {s}
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatusBanner({ ok, msg, testId }) {
  return (
    <div
      data-testid={testId}
      className={`mt-8 flex items-start gap-3 p-4 rounded ${
        ok
          ? "border border-[var(--k-olive)] bg-[rgba(143,166,120,0.12)]"
          : "border border-red-300 bg-red-50"
      }`}
    >
      {ok ? (
        <CheckCircle2 className="w-5 h-5 text-[var(--k-green)] mt-0.5" />
      ) : (
        <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
      )}
      <p className="text-[var(--k-text)] text-sm">{msg}</p>
    </div>
  );
}
